import React from "react";

/**
 * Shared FormField component for consistent field rendering
 * Supports text, checkbox, select, and textarea field types
 *
 * @param {Object} field - Field configuration object
 * @param {string} field.type - Field type: 'text', 'checkbox', 'select', 'textarea'
 * @param {string} field.name - Field name/id
 * @param {string} field.label - Field label text
 * @param {string} field.helptip - Tooltip text for the field
 * @param {Array} field.values - Options for select fields
 * @param {string} field.placeholder - Placeholder text
 * @param {number} field.rows - Number of rows for textarea
 * @param {*} value - Current field value
 * @param {Function} onChange - Change handler function
 * @param {Object} validation - Validation result object
 * @param {boolean} validation.isValid - Whether the field value is valid
 * @param {string} validation.message - Validation error message
 * @param {string} className - Additional CSS classes
 * @param {string} layout - Layout style: 'horizontal' (label and input side by side) or 'vertical' (label above input)
 */
const FormField = ({
  field,
  value,
  onChange,
  validation = { isValid: true, message: "" },
  className = "",
  layout = "horizontal",
}) => {
  const {
    type = "text",
    name,
    label,
    helptip,
    values = [],
    placeholder = "",
    rows = 1,
  } = field;

  const fieldId = name || field.id; // Support both name and id
  const fieldValue = value || "";

  // Base input class with validation styling
  const inputClass = `form-control ${
    !validation.isValid ? "is-invalid" : ""
  } ${className}`;

  // Render the appropriate input based on field type
  const renderInput = () => {
    switch (type) {
      case "checkbox":
        return (
          <div className="form-check">
            <input
              type="checkbox"
              id={fieldId}
              name={fieldId}
              checked={fieldValue || false}
              onChange={onChange}
              className="form-check-input"
              aria-describedby={helptip ? `${fieldId}-help` : undefined}
            />
          </div>
        );

      case "select":
        return (
          <select
            id={fieldId}
            name={fieldId}
            value={fieldValue}
            onChange={onChange}
            className={`form-select ${
              !validation.isValid ? "is-invalid" : ""
            } ${className}`}
            aria-describedby={helptip ? `${fieldId}-help` : undefined}
          >
            <option value="">Select an option</option>
            {values.map((option, idx) => (
              <option key={idx} value={option.value}>
                {option.caption || option.label || option.value}
              </option>
            ))}
          </select>
        );

      case "textarea":
        return (
          <textarea
            id={fieldId}
            name={fieldId}
            value={fieldValue}
            onChange={onChange}
            className={inputClass}
            placeholder={placeholder}
            rows={rows}
            aria-describedby={helptip ? `${fieldId}-help` : undefined}
          />
        );

      default: // text input
        return (
          <input
            type="text"
            id={fieldId}
            name={fieldId}
            value={fieldValue}
            onChange={onChange}
            className={inputClass}
            placeholder={placeholder}
            aria-describedby={helptip ? `${fieldId}-help` : undefined}
          />
        );
    }
  };

  // Render label with tooltip if provided
  const renderLabel = () => {
    if (!label) return null;

    const isVerticalLayout = layout !== "horizontal" || type !== "checkbox";
    const labelProps = {
      htmlFor: fieldId,
      className: isVerticalLayout ? "form-label" : "col-form-label col",
    };

    if (helptip) {
      labelProps["data-bs-toggle"] = "tooltip";
      labelProps.title = helptip;
    }

    return <label {...labelProps}>{label}</label>;
  };

  // Render validation feedback
  const renderValidationFeedback = () => {
    if (validation.isValid || !validation.message) return null;

    return <div className="invalid-feedback">{validation.message}</div>;
  };

  // Render help text
  const renderHelpText = () => {
    if (!helptip || layout !== "vertical") return null;

    return (
      <small id={`${fieldId}-help`} className="form-text text-muted">
        {helptip}
      </small>
    );
  };

  if (layout === "horizontal") {
    if (type === "checkbox") {
      const labelProps = {
        htmlFor: fieldId,
        className:
          "d-flex justify-content-between align-items-center checkbox-row",
      };

      // Add tooltip attributes if helptip is provided
      if (helptip) {
        labelProps["data-bs-toggle"] = "tooltip";
        labelProps.title = helptip;
      }

      return (
        <label {...labelProps}>
          <span className="col-form-label">{label}</span>
          <div className="checkbox-container">
            {renderInput()}
            {renderValidationFeedback()}
          </div>
        </label>
      );
    }

    // For non-checkbox inputs in horizontal layout, use vertical layout for better spacing
    return (
      <div className="mb-2">
        {renderLabel()}
        {renderInput()}
        {renderValidationFeedback()}
        {renderHelpText()}
      </div>
    );
  }

  // Vertical layout
  return (
    <div className="mb-2">
      {renderLabel()}
      {renderInput()}
      {renderValidationFeedback()}
      {renderHelpText()}
    </div>
  );
};

export default FormField;
